/**
 * frontend/src/utils/consoleUrl.js
 * ----------------------------------
 * Utilitaires pour construire les URLs WebSocket des consoles.
 *
 * CORRECTIF : WS_VNC_PATH n'était pas défini → ReferenceError au runtime.
 */

const WS_TELNET_PATH = "/api/v1/console/ws/telnet";
const WS_VNC_PATH    = "/api/v1/console/ws/vnc";

export function httpToWsOrigin(origin) {
  return String(origin || "")
    .replace(/^http:/i, "ws:")
    .replace(/^https:/i, "wss:");
}

export function getApiOrigin() {
  // Vite
  if (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }

  // CRA — REACT_APP_WS_ORIGIN peut être défini dans .env.development
  if (typeof process !== "undefined" && process.env?.REACT_APP_WS_ORIGIN) {
    return process.env.REACT_APP_WS_ORIGIN;
  }

  // Stratégie dynamique : même hostname que la page mais port 8000 (backend uvicorn).
  // Cela fonctionne que l'accès soit via localhost:3000 ou labnexthopacademy.local:3000
  // sans dépendre d'une variable d'env injectée au build.
  // En production (port 80/443), on utilise window.location.origin (nginx proxy).
  if (typeof window !== "undefined") {
    const { hostname, port, protocol } = window.location;
    const isDevPort = port === "3000";  // CRA dev server
    if (isDevPort) {
      // Dev : WS directement sur le backend uvicorn (port 8000)
      return `${protocol}//${hostname}:8000`;
    }
    // Prod : même origin (nginx gère le proxy WS)
    return window.location.origin;
  }

  return "http://localhost:8000";
}

export function getToken() {
  return (
    localStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    ""
  );
}

// SEC-WS-01 : le token ne fait plus partie de l'URL (donc plus des journaux
// d'acces nginx/uvicorn, qui logguent la ligne de requete complete par
// defaut) -- buildTelnetWsUrl() ne prend plus `token`. Le JWT est transmis
// via le sous-protocole WebSocket (Sec-WebSocket-Protocol), deja lu par le
// backend pour n'importe quelle route passant par _extract_token()
// (backend_api/routes/console_ws.py), sans aucune modification backend
// necessaire. Voir buildTokenSubprotocols() ci-dessous.
export function buildTelnetWsUrl({ host, port }) {
  const wsOrigin = httpToWsOrigin(getApiOrigin());
  const qs = new URLSearchParams({
    host: String(host || ""),
    port: String(port || ""),
  });
  return `${wsOrigin}${WS_TELNET_PATH}?${qs.toString()}`;
}

/**
 * Sous-protocoles WebSocket transmettant le JWT hors de l'URL, a passer en
 * second argument de `new WebSocket(url, protocols)`. Convention
 * "token.<jwt>" deja supportee cote backend (_extract_token(), prevue a
 * l'origine pour noVNC) -- reutilisee ici pour le telnet, sans changement
 * backend. Les points internes du JWT (header.payload.signature) sont des
 * caracteres de "token" HTTP valides (RFC 2616/7230) : aucun conflit avec
 * le separateur "," de l'en-tete Sec-WebSocket-Protocol.
 */
export function buildTokenSubprotocols(token) {
  return token ? [`token.${token}`] : [];
}

// SECWS03 : meme traitement que buildTelnetWsUrl() ci-dessus -- le token ne
// fait plus partie de l'URL VNC. noVNC (verifie sur la version installee,
// @novnc/novnc@1.4.0) ne lit le sous-protocole confirme par le serveur que
// pour un message de log informatif et fixe `binaryType` de maniere
// inconditionnelle : il n'exige donc pas que le serveur confirme precisement
// "binary" en reponse, ce qui laisse la place a un second sous-protocole
// "token.<jwt>" propose en plus -- voir buildTokenSubprotocols() ci-dessus,
// reutilise ici tel quel, et NoVncViewer.jsx qui l'ajoute a wsProtocols.
export function buildVncWsUrl({ host, port }) {
  const wsOrigin = httpToWsOrigin(getApiOrigin());
  const qs = new URLSearchParams({
    host: String(host || ""),
    port: String(port || ""),
  });
  return `${wsOrigin}${WS_VNC_PATH}?${qs.toString()}`;
}
